/**
 * Utility to detect and parse multi-selection messages from agent responses
 * Supports formats like:
 * - JSON: { "type": "multiSelection", "question": "...", "options": [...] }
 * - Markdown with checkboxes
 * - Numbered/Bulleted lists with selection indicators
 */

/**
 * Detects if a message contains multi-selection options
 * @param {string} message - The agent message text
 * @returns {object|null} - Parsed multi-selection data or null if not detected
 */
export const detectMultiSelection = (message) => {
  if (!message || typeof message !== 'string') {
    return null;
  }

  // Try to detect JSON format first
  const jsonMatch = tryParseJsonMultiSelection(message);
  if (jsonMatch) {
    return jsonMatch;
  }

  // Try to detect markdown checkbox format
  const checkboxMatch = tryParseCheckboxFormat(message);
  if (checkboxMatch) {
    return checkboxMatch;
  }

  // Try to detect numbered/bulleted list with selection indicator
  const listMatch = tryParseListFormat(message);
  if (listMatch) {
    return listMatch;
  }

  // Try to detect numbered question with lettered options (A, B, C, D)
  const letteredMatch = tryParseNumberedQuestionWithLetteredOptions(message);
  if (letteredMatch) {
    return letteredMatch;
  }

  return null;
};

/**
 * Try to parse JSON multi-selection format
 * Expected format: { "type": "multiSelection", "question": "...", "options": [...], "minSelections": 1, "maxSelections": null }
 */
const tryParseJsonMultiSelection = (message) => {
  try {
    // Look for JSON block in the message
    const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/;
    const jsonMatch = message.match(jsonBlockRegex);
    
    let jsonData;
    if (jsonMatch) {
      jsonData = JSON.parse(jsonMatch[1]);
    } else {
      // Try to parse the entire message as JSON
      jsonData = JSON.parse(message);
    }

    if (jsonData.type === 'multiSelection' && Array.isArray(jsonData.options)) {
      const normalizedOptions = jsonData.options.map((opt, idx) => ({
        id: opt.id || `option-${idx}`,
        label: opt.label || opt.value || opt,
        value: opt.value || opt.label || opt
      }));

      return {
        type: 'multiSelection',
        questions: [{
          question: jsonData.question || 'Please select your options:',
          options: normalizedOptions,
          minSelections: jsonData.minSelections || 1,
          maxSelections: jsonData.maxSelections || null
        }],
        // Legacy fields for backward compatibility
        question: jsonData.question || 'Please select your options:',
        options: normalizedOptions,
        minSelections: jsonData.minSelections || 1,
        maxSelections: jsonData.maxSelections || null,
        metadata: jsonData.metadata || {}
      };
    }
  } catch (e) {
    // Not valid JSON or not the right format
  }
  return null;
};

/**
 * Try to parse markdown checkbox format
 * Expected format:
 * Question text?
 * - [ ] Option 1
 * - [ ] Option 2
 * - [ ] Option 3
 */
const tryParseCheckboxFormat = (message) => {
  // Look for unchecked markdown checkboxes
  const checkboxPattern = /^[-*]\s*\[\s*\]\s*(.+)$/gm;
  const matches = [...message.matchAll(checkboxPattern)];
  
  if (matches.length >= 2) { // At least 2 options
    // Extract question (everything before first checkbox)
    const firstCheckboxIndex = message.indexOf(matches[0][0]);
    const question = message.substring(0, firstCheckboxIndex).trim() || 'Please select your options:';
    
    const options = matches.map((match, idx) => ({
      id: `option-${idx}`,
      label: match[1].trim(),
      value: match[1].trim()
    }));

    return {
      type: 'multiSelection',
      questions: [{
        question,
        options,
        minSelections: 1,
        maxSelections: null
      }],
      // Legacy fields
      question,
      options,
      minSelections: 1,
      maxSelections: null,
      metadata: { format: 'checkbox' }
    };
  }
  
  return null;
};

/**
 * Try to parse numbered or bulleted list format with selection indicator
 * Expected format:
 * Question text? [SELECT MULTIPLE]
 * 1. Option 1
 * 2. Option 2
 * 3. Option 3
 */
const tryParseListFormat = (message) => {
  // Check for selection indicator
  const hasSelectionIndicator = /\[SELECT\s+MULTIPLE\]|\(select\s+multiple\)|\*\*select\s+multiple\*\*/i.test(message);
  
  if (!hasSelectionIndicator) {
    return null;
  }

  // Look for numbered or bulleted lists
  const numberedPattern = /^\d+\.\s*(.+)$/gm;
  const bulletedPattern = /^[-*]\s+(.+)$/gm;
  
  const numberedMatches = [...message.matchAll(numberedPattern)];
  const bulletedMatches = [...message.matchAll(bulletedPattern)];
  
  const matches = numberedMatches.length >= bulletedMatches.length ? numberedMatches : bulletedMatches;
  
  if (matches.length >= 2) {
    // Extract question (everything before first list item)
    const firstItemIndex = message.indexOf(matches[0][0]);
    const questionPart = message.substring(0, firstItemIndex).trim();
    // Remove selection indicator from question
    const question = questionPart.replace(/\[SELECT\s+MULTIPLE\]|\(select\s+multiple\)|\*\*select\s+multiple\*\*/i, '').trim() || 'Please select your options:';
    
    const options = matches.map((match, idx) => ({
      id: `option-${idx}`,
      label: match[1].trim(),
      value: match[1].trim()
    }));

    return {
      type: 'multiSelection',
      questions: [{
        question,
        options,
        minSelections: 1,
        maxSelections: null
      }],
      // Legacy fields
      question,
      options,
      minSelections: 1,
      maxSelections: null,
      metadata: { format: 'list' }
    };
  }
  
  return null;
};

/**
 * Try to parse numbered question with lettered options format
 * Expected format:
 * 1. Question text? (Select all that apply)
 *    A) Option 1
 *    B) Option 2
 * 2. Another question?
 *    A) Option 1
 *    B) Option 2
 * 
 * Also supports A. format
 */
const tryParseNumberedQuestionWithLetteredOptions = (message) => {
  // Split message into sections by numbered questions
  const questionSections = message.split(/(?=^\s*\d+\.)/m).filter(section => section.trim());
  
  const detectedQuestions = [];
  
  for (const section of questionSections) {
    // Look for numbered question (1., 2., etc.)
    const questionMatch = section.match(/^\s*\d+\.\s*(.+?)(?=\n|$)/m);
    
    if (!questionMatch) continue;
    
    let questionText = questionMatch[1].trim();
    
    // Remove common indicators like "(Select all that apply)"
    questionText = questionText.replace(/\(Select all that apply\)/i, '').trim();
    
    // Find all lettered options - supports both "A)" and "A." formats
    // Match pattern like "A) Text" or "A. Text" (with optional leading whitespace)
    const optionPattern = /^\s*([A-Z])[\.)]\s*(.+?)(?=\n|$)/gm;
    const optionMatches = [...section.matchAll(optionPattern)];
    
    if (optionMatches.length < 2) continue;
    
    const options = optionMatches.map((match) => ({
      id: `option-${match[1].toLowerCase()}`,
      label: match[2].trim(),
      value: match[2].trim()
    }));
    
    detectedQuestions.push({
      question: questionText,
      options,
      minSelections: 1,
      maxSelections: null
    });
  }
  
  // If we found multiple questions, return them as a multi-question set
  if (detectedQuestions.length > 0) {
    return {
      type: 'multiSelection',
      isMultiQuestion: detectedQuestions.length > 1,
      questions: detectedQuestions,
      // Legacy support - use first question as main question
      question: detectedQuestions[0].question,
      options: detectedQuestions[0].options,
      minSelections: 1,
      maxSelections: null,
      metadata: { 
        format: 'numbered-lettered',
        totalQuestions: detectedQuestions.length
      }
    };
  }
  
  return null;
};

/**
 * Format selected options to send back to agent
 * @param {Array} selectedOptions - Array of selected option objects
 * @param {string} question - The original question
 * @returns {string} - Formatted message to send to agent
 */
export const formatSelectedOptionsMessage = (selectedOptions, question) => {
  if (!selectedOptions || selectedOptions.length === 0) {
    return 'No options selected';
  }

  const optionsList = selectedOptions.map(opt => `• ${opt.label}`).join('\n');
  
  return `Selected options for "${question}":\n\n${optionsList}`;
};

